import React, { createContext, useContext, useState, useEffect } from 'react';
import Themes from './FleetlyTheme.js';

/**
 * Theme Context for dynamic theme switching
 * 
 * Provides theme state management and utilities for the entire app
 * Supports light/dark mode switching with persistent storage
 */

const ThemeContext = createContext();

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default to light
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = localStorage.getItem('fleetly_theme');
    return savedTheme || 'light';
  });

  // Get the current theme object
  const theme = Themes[currentTheme] || Themes.light;

  // Switch theme and persist to localStorage
  const switchTheme = (themeName) => {
    if (Themes[themeName]) {
      setCurrentTheme(themeName);
      localStorage.setItem('fleetly_theme', themeName);
    }
  };

  // Toggle between light and dark themes
  const toggleTheme = () => {
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    switchTheme(newTheme);
  };

  // Update CSS custom properties when theme changes
  useEffect(() => {
    const root = document.documentElement;
    
    // Set CSS custom properties for the current theme
    Object.entries(theme).forEach(([key, value]) => {
      root.style.setProperty(`--fleetly-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`, value);
    });

    // Set individual color properties for easy access
    root.style.setProperty('--fleetly-primary-navy', theme.btnBgNavy || Themes.light.btnBgNavy);
    root.style.setProperty('--fleetly-secondary-teal', theme.btnBgGreen || Themes.light.btnBgGreen);
    root.style.setProperty('--fleetly-accent-blue', theme.btnBgBlue || Themes.light.btnBgBlue);
    root.style.setProperty('--fleetly-accent-orange', theme.btnBgRed || Themes.light.btnBgRed);

    // Set semantic color properties
    root.style.setProperty('--fleetly-background', theme.background);
    root.style.setProperty('--fleetly-surface', theme.surface);
    root.style.setProperty('--fleetly-text', theme.text);
    root.style.setProperty('--fleetly-text-subtle', theme.textSubtle);
    root.style.setProperty('--fleetly-border', theme.border);
    root.style.setProperty('--fleetly-nav-background', theme.navBarBackground || theme.surface);
    root.style.setProperty('--fleetly-nav-text', theme.navBarText || theme.text);
    root.style.setProperty('--fleetly-nav-active', theme.navBarActive || theme.tabBarActive);

    // Add theme class to body for CSS-based theming
    document.body.className = document.body.className.replace(/theme-\w+/g, '');
    document.body.classList.add(`theme-${currentTheme}`);
  }, [theme, currentTheme]);

  const contextValue = {
    currentTheme,
    theme,
    availableThemes: Object.keys(Themes),
    switchTheme,
    toggleTheme,
    isLightTheme: currentTheme === 'light',
    isDarkTheme: currentTheme === 'dark',
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
